import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { 
  Home, Star, CheckCircle, MessageCircle, Smartphone, 
  Wind, Droplets, Heart, ShieldCheck, Search 
} from 'lucide-react';
import "./FreshNCleanApp.css";

const FreshNCleanFull = () => {
  return (
    <div className="bg-white font-sans text-dark overflow-hidden">
      {/* --- 1. HOME CARE SECTION --- */}
      <section className="py-5 bg-light border-bottom">
        <Container>
          <Row>
            <Col md={12}>
                        <h2 className="expertise-title">Your Home, Held to a Higher Standard.</h2>
                    <p className="lead mb-4 text-secondary">
              The textiles that shape your living space deserve the same expertise as your wardrobe.
            </p>

             <p className="text-muted lh-lg max-w-3xl">
              A home is defined by more than its furniture. The quality of your curtains, the freshness 
              of your bedding, the cleanliness of your sofa upholstery — these are the details that 
              make a space feel truly cared for. Fresh N Clean's Home Care service brings 25 years of 
              professional textile expertise directly to your household linens, soft furnishings, 
              and floor coverings.
            </p>
            </Col>
          </Row>
        </Container>

 <Row>
  <Col md={12}>
  <h2 className="expertise-title">What's Included</h2> 
  </Col>
 </Row>
      <Container className='mt-4'>

  <Row className="g-4 mb-2">

    <Col lg={7}>
      <Card className="custom-card border-0 shadow-sm rounded-4 h-100">
        <Card.Body className="p-4 p-md-5">
       
       

          <ul className="list-unstyled">
            {[
              { t: "Sheer and lined curtain cleaning:", d: "hand-guided wash cycles with fabric tension management to prevent distortion of the weave or heading" },
              { t: "Blackout curtain and interlined curtain treatment", d: "with specialised support during wash and dry cycles" },
              { t: "Duvet and quilt deep cleaning", d: "with fill-appropriate cycles — goose down, microfibre, and synthetic fills handled separately" },
              { t: "Pillow cleaning", d: "with full shape restoration and allergen-reducing rinse treatment" },
              { t: "Bed linen laundering:", d: "flat sheets, fitted sheets, duvet covers, and pillowcases pressed and folded" },
              { t: "Mattress protector and topper cleaning", d: "with antimicrobial treatment on request" },
              { t: "Sofa cover and slipcover washing:", d: "gentle cycles with controlled heat to prevent shrinkage of fitted covers" },
              { t: "Carpet and rug cleaning:", d: "soil extraction and stain removal using hot-water extraction methods on suitable pile types" },
              { t: "Prayer mat cleaning", d: "with fibre-safe, fragrance-neutral processes" },
              { t: "Decorative cushion cover laundering", d: "with filling removed and shape maintained" },
              { t: "Full QR tracking", d: "on each item in your home care order — nothing is lost or misidentified" },
              { t: "Large item collection and return", d: "managed through the Fresh N Clean app across Ajman and Sharjah" }
            ].map((item, i) => (
              <li key={i} className="list-item mb-3 d-flex gap-2 align-items-start small border-bottom pb-2">
                <span className="text-primary fw-bold check-icon">✓</span>
                <span><strong>{item.t}</strong> {item.d}</span>
              </li>
            ))}
          </ul>
        </Card.Body>
      </Card>
    </Col>

    <Col lg={5} className='mt-5'>
      <Card className="process-card border-0 text-white p-4 rounded-4 mb-4 shadow h-auto mt-5" style={{backgroundColor:"#051c7b",color:"white"}}>
        <h4 className="fw-bold mb-3 border-bottom border-white border-opacity-25 pb-2">
          Our Process
        </h4>
        <p className="small lh-lg mb-0 opacity-90">
          Home textiles present unique challenges: scale, construction type, and sensitivity all 
          vary dramatically from item to item. A blackout curtain requires fundamentally different 
          handling from a lightweight lace sheer or a Berber-pile rug. Our team assesses each 
          item individually, and our equipment — including large-drum commercial washers and 
          industrial extraction systems — is sized for household textiles in a way that 
          domestic appliances simply are not. We also understand the cultural significance 
          of prayer mats and traditional household linens and handle them with corresponding respect.
        </p>
      </Card>

      <Card className="ideal-card border-0 bg-opacity-10 p-4 rounded-4 shadow-sm border border-info" style={{backgroundColor:"#051c7b",color:"white"}}>
        <h4 className="fw-bold mb-3 text-light">Ideal For</h4>
        <p className="text-light small mb-0 lh-base">
          Sheer and blackout curtains, interlined drapes and valances, duvets and quilts, 
          pillows and cushion covers, fitted and flat bed sheets, sofa covers and slipcovers, 
          prayer mats, rugs and carpets, mattress protectors, and decorative throw covers.
        </p>
      </Card>
    </Col>
  </Row>
</Container>
      </section>

      {/* --- 2. PREMIUM CARPET CLEANING SECTION --- */}
      <section className="py-1 text-dark">
          <div className="text-center mb-5 px-3">
            <h2 className="expertise-title">Premium Carpet Cleaning</h2>
            <h4 className="h5 fw-bold mb-4 opacity-90 mt-5">Your Carpet Holds More Than You Think — We Remove All of It.</h4>
            <p className="mx-auto col-lg-10 opacity-75 small lh-lg">
              Your carpet is not just a floor covering. It is the largest filter in your home, 
              trapping dust, allergens, bacteria, pet dander, sand, food particles, and moisture 
              every single day. Regular vacuuming removes what is on the surface. Our advanced 
              carpet care service removes what lives underneath — deep inside the fibres where 
              no home vacuum ever reaches. At Fresh N Clean, we use industrial-grade hot water 
              extraction equipment combined with world-class professional carpet chemistry to 
              deliver a level of clean that visibly and measurably transforms your carpet — 
              in a single service.
            </p>
          </div>

          <Row className="g-3 mb-5 px-2">
            {[
              { t: "Hot water extraction deep clean", d: "Removes soil, bacteria and allergens from the base of every fibre" },
              { t: "Professional pre-treatment per stain type", d: "Removes stains home products cannot reach" },
              { t: "Improved indoor air quality", d: "Healthier home for your family — especially children and allergy sufferers" },
              { t: "Dust mite and allergen elimination", d: "No vacuum cleaner achieves this level of hygiene" },
              { t: "Moisture prevention and fast drying", d: "No mould risk — carpet is dry and usable quickly" },
              { t: "Extended carpet lifespan", d: "Protects your investment from abrasive damage" },
              { t: "Odour elimination at the source", d: "Genuinely fresh — not masked" },
              { t: "Safe chemistry — certified skin and pet safe", d: "Total peace of mind for your family" }
            ].map((item, i) => (
              <Col sm={6} lg={3} key={i}>
  <div className="premium-feature-box p-3 border rounded-3 h-100">
    <h6 className="fw-bold fs-5 mb-2 small title-text">
      {item.t}
    </h6>
    <p className="desc-text mb-0">
      {item.d}
    </p>
  </div>
</Col>
            ))}
          </Row>
 <br />

       <h4 className="expertise-title text-center mb-4">
  Our Carpet Cleaning Process
</h4>

<Row className="g-4 mt-3 process-wrapper">
  {[
    { s: "Step 01", t: "Inspection", d: "Every carpet is examined for fibre type, pile depth, stain types, and soiling level before any treatment begins. Nothing is assumed." },
    { s: "Step 02", t: "Pre-Vacuuming", d: "Loose dry soil, dust, sand, and surface debris are fully vacuumed out before wet treatment begins — ensuring the deep clean works on embedded dirt, not surface particles." },
    { s: "Step 03", t: "Pre-Treatment & Stain Spotting", d: "Targeted pre-treatment solution is applied to heavy traffic lanes, embedded stains, and soiled areas. Each stain type — oil, food, tea, pet, or mud — is treated with the specific chemistry formulated to dissolve it." },
    { s: "Step 04", t: "Hot Water Extraction", d: "Industrial hot water extraction equipment injects heated water and professional cleaning solution deep into the carpet pile under controlled pressure, loosening embedded soil, bacteria, and allergens from the base of every fibre." },
    { s: "Step 05", t: "Deep Extraction", d: "Powerful suction immediately pulls the water, dissolved soil, bacteria, and cleaning agents back out — leaving the carpet deep-cleaned with minimal residual moisture." },
    { s: "Step 06", t: "Spot Re-Treatment", d: "Any remaining stubborn stains are re-assessed and treated individually with specialist solutions before the carpet fully dries." },
    { s: "Step 07", t: "Grooming", d: "Carpet fibres are groomed in a uniform direction to restore pile direction, lift flattened areas, and ensure an even, refreshed appearance across the entire surface." },
    { s: "Step 08", t: "Rapid Drying", d: "Industrial air movers accelerate drying time, preventing moisture from settling into the backing or padding — eliminating any risk of mould or mildew." },
    { s: "Step 09", t: "Final Inspection", d: "Every carpet is inspected under directional light for stain removal, pile condition, and overall finish before it is released or returned to you." },
    { s: "Step 10", t: "Optional Protection Treatment", d: "A certified fabric protector is applied on request — creating an invisible barrier that repels future staining and keeps your carpet cleaner between professional services." }
  ].map((step, idx) => (
    <Col md={3} key={idx}>
      <div className="process-card-new h-100">
        <div className="step-badge-new">
          {step.s}
        </div>

        <div className="content">
          <h6 className="fw-bold mb-2 fs-5 small">{step.t}</h6>
          <p className="small mb-0 text-justify desc">
            {step.d}
          </p>
        </div>

      </div>
    </Col>
  ))}
</Row>
   
      </section>

      {/* --- 3. WEDDING GOWN RESTORATION SECTION --- */}
         <section className="py-5 mt-4 bg-white">
  <Row className="align-items-center g-5">
            <Col lg={12}>
<Badge bg="primary" className="mb-3 px-3 py-2">HEIRLOOM CARE</Badge>
              <h1 className="expertise-title">Wedding Gown Restoration</h1>
              <h4 className="text-primary fw-bold mb-4">Preserve the Gown. Hold the Memory.</h4>
                      <p className="mx-auto col-lg-10 opacity-75 small lh-lg">
                Some garments are worn once and kept forever. We treat them accordingly. 
                Every wedding gown that comes to us — fresh from the ceremony, returned 
                from years in storage, or handed down across generations — receives a 
                full condition review, fibre-matched cleaning, and the care its history deserves.
              </p>
            </Col>
</Row>
         </section>
      <section className="py-5 bg-white">
        <Container>
          <Row className="align-items-center g-5">
            <Col lg={6}>
              
              
              <h5 className="fw-bold mb-4 border-bottom pb-2"
              
              expertise-title>Why Choose Fresh N Clean for Your Wedding Gown</h5>
              <div className="row g-2">
                {[
                  "Protects your investment — Professional care extends the life of your gown by decades.",
                  "Removes what home cleaning can't — Champagne, foundation, henna, and hemline stains treated with the right chemistry, not guesswork.",
                  "Embellishments stay safe — Beads, crystals, sequins, and pearls individually secured before and after every stage.",
                  "Shape and structure preserved — Boning, corsetry, and bodice panels handled to maintain the original silhouette.",
                  "Heirloom gowns restored — Yellowing and oxidised stains from years in storage reversed by specialist treatment.",
                  "Safe long-term storage — Acid-free archival boxing keeps your gown white and intact for generations.",
                  "Nothing done without your approval — Full condition review and photos before any treatment begins.",
                  "Every UAE bridal garment covered — Western gowns, embellished Abayas, reception dresses, veils, and traditional Emirati bridal wear, all under one roof."
                ].map((text, i) => (
                  <Col xs={12} key={i}>
                    <div className="d-flex gap-2 p-2 border-start border-primary border-4 bg-primary text-light small fw-medium mb-1">
                      <span className="text-light fw-bold">✦</span> {text}
                    </div>
                  </Col>
                ))}
              </div>
            </Col>
            <Col lg={6}>
               <div className="p-2 border bg-light rounded-4">
                  <img src="https://www.thespruce.com/thmb/HOnCMVMRj1air46_LxGJ3PzBLV4=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/GettyImages-1400537194-31399509dd9b4d89a551e768583c4b99.jpg" alt="Wedding Gown" className="img-fluid rounded-4 shadow-sm w-100" />
               </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* --- FINAL CTA --- */}

    </div>
  );
};

export default FreshNCleanFull;